// useJob(id, { live?, enabled? }) → UseQueryResult<Job>        key ["job", id]
// The REST record. Polls every 2 s while the run is live and stops at a terminal state. Liveness
// comes from the job's event stream when one is open (the stream beats the lagging record),
// else from the record; pass `live` to decide explicitly. A missing job is not retried: 404, 400, and the real
// controller's 500 "no rows in result set" for an unknown id (isMissingJob).
import { useQuery } from "@tanstack/react-query";
import { api, isApiError } from "@/lib/api";
import { isTerminal } from "@/lib/eventStore";
import type { Job } from "@/types";
import { keys } from "./keys";
import { streams } from "./streamRegistry";

const JOB_POLL_MS = 2000;

/** The controller has no such job: 404, a malformed id (400), or its SQL "no rows" surfacing as a 500. */
export const isMissingJob = (err: unknown): boolean =>
  isApiError(err) && (err.status === 404 || err.status === 400 || (err.status === 500 && /no rows in result set/i.test(err.detail)));

export const retryUnlessMissing = (count: number, err: unknown): boolean => !isMissingJob(err) && count < 3;

/** Shared options for ["job", id] (also used inside useJobStream). */
export function jobQueryOptions(id: string) {
  return {
    queryKey: keys.job(id),
    queryFn: () => api.job(id),
    retry: retryUnlessMissing,
  };
}

/** Live for polling purposes: the stream's effective state when known, else the record's. */
export function jobIsLive(id: string, record: Job | undefined): boolean {
  const fromStream = streams.peekLive(id, record);
  if (fromStream !== undefined) return fromStream;
  return !!record && !isTerminal(record.state);
}

export function useJob(id: string, opts: { live?: boolean; enabled?: boolean } = {}) {
  const { live, enabled = true } = opts;
  return useQuery({
    ...jobQueryOptions(id),
    enabled: !!id && enabled,
    refetchInterval: (q) => ((live ?? jobIsLive(id, q.state.data)) ? JOB_POLL_MS : false),
  });
}
